import React, { useCallback } from 'react';

import isEmpty from 'lodash/isEmpty';

import {
  Stack,
  Typography,
  VisibilityIcon,
  VisibilityOffIcon,
  IconButton,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import {
  useScreenState,
  useScreenDispatch,
  useScreenTitle,
} from '@gogame/screens';

import {
  KPIFormEdit,
  KPIFormDuplicate,
  KPIFormCreate,
} from 'scenes/KPI/components/KPIForm';
import {
  COLUMN_VISIBILITY,
  COLUMN_ACTIVITY,
  COLUMN_METRIC,
  COLUMN_TRAFFIC,
  COLUMN_GENRES,
  COLUMN_APPLICATIONS,
  COLUMN_NETWORKS,
  COLUMN_PLATFORMS,
  COLUMN_COUNTRIES,
  COLUMN_REGISTRATIONS,
  COLUMN_MIN,
  COLUMN_AVERAGE,
  COLUMN_MAX,
  COLUMN_TOTAL,
  COLUMN_FRAUD_TYPE,
} from 'scenes/KPI/constants';
import { useLoadKPITable } from 'scenes/KPI/data';
import { useExportKPITable } from 'scenes/KPI/hooks';
import {
  SET_SERIES,
  ADD_SERIES,
  HIDE_SERIES,
  SHOW_SERIES,
  DELETE_SERIES,
  DELETE_LAST_SERIES,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/KPI/reducers';

import Toolbar from './Toolbar';

import DataGrid from '@gogame/data-grid';
import BulkActionsToolbar from './BulkActionsToolbar';
import KPIActions from './Actions';

const TABLE_HEIGHT = 700;

export const columnMenuConfig = {
  columnMenuCollapsingItem: true,
  columnMenuColorScaleItem: true,
};

const KPIDataTable = ({ idColorGetter }) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const [screenTitle] = useScreenTitle();
  const exportKPITable = useExportKPITable();
  const { hidden, period, series, temporary } = screenState;

  const visibleSeries = temporary?.series || series;
  const visiblePeriod = temporary?.period || period;

  const { data, isFetching } = useLoadKPITable({
    period: period,
    series: series,
    temporarySeries: temporary?.series,
    enabled: !temporary && series.length > 0,
    keepPreviousData: visibleSeries.length > 0,
  });

  const handleExportVisible = useCallback(() => {
    exportKPITable({
      period: visiblePeriod,
      series: visibleSeries.filter(({ id }) => !hidden.includes(id)),
    });
  }, [hidden, visiblePeriod, visibleSeries]);

  const handleExportAll = useCallback(() => {
    exportKPITable({ period: visiblePeriod, series: visibleSeries });
  }, [visiblePeriod, visibleSeries]);

  const handleEdit = (_e, { current, isFirst, first }) => {
    openChildComponent(KPIFormEdit, {
      initialValues: current,
      firstEntity: isFirst,
      firstTableRowValues: first,
      onSubmit: (values) => {
        dispatch({
          type: SET_SERIES,
          payload: values,
          id: current.id,
        });
      },
    });
  };

  const handleDuplicate = (_e, { current, first }) => {
    openChildComponent(KPIFormDuplicate, {
      initialValues: current,
      firstTableRowValues: first,
      onSubmit: (values) => {
        dispatch({
          type: ADD_SERIES,
          payload: values,
        });
      },
    });
  };

  const handleClickVisibility = useCallback(
    (_e, { id, state }) => {
      dispatch({
        type: state ? HIDE_SERIES : SHOW_SERIES,
        payload: [id],
      });
    },
    [dispatch]
  );

  const handleDelete = (_e, { id }) => {
    dispatch({
      type: visibleSeries.length === 1 ? DELETE_LAST_SERIES : DELETE_SERIES,
      payload: [id],
    });
  };

  const handleBatchShow = useCallback(
    (_e, { ids }) => {
      dispatch({
        type: SHOW_SERIES,
        payload: ids,
      });
    },
    [dispatch]
  );

  const handleBatchHidden = useCallback(
    (_e, { ids }) => {
      dispatch({
        type: HIDE_SERIES,
        payload: ids,
      });
    },
    [dispatch]
  );

  const handleBatchDelete = useCallback(
    (_e, { ids }) => {
      dispatch({
        type:
          visibleSeries.length === ids.length
            ? DELETE_LAST_SERIES
            : DELETE_SERIES,
        payload: ids,
      });
    },
    [dispatch]
  );

  const handleRowColor = useCallback(
    (row) => {
      try {
        return idColorGetter(row.id);
      } catch (e) {
        /* eslint-disable-next-line */
        console.log(`
        rowIndex ${row.id}
      `);
      }
    },
    [data]
  );

  const handleCreate = useCallback(() => {
    openChildComponent(KPIFormCreate, {
      firstEntity: isEmpty(visibleSeries),
      firstTableRowValues: visibleSeries[0],
      onSubmit: (values) => {
        dispatch({
          type: ADD_SERIES,
          payload: values,
        });
      },
    });
  }, [visibleSeries, openChildComponent, dispatch]);

  const selectedRowsToolbarActions = useCallback(
    ({ selectedValues }) => {
      const hiddenRows = [];
      const appliedRows = (data?.rows || []).filter((row) => {
        return selectedValues.includes(row.id) && !row.dirty;
      });

      appliedRows.forEach((row) => {
        const { id } = row;
        if (screenState.hidden.includes(id)) {
          hiddenRows.push(row);
        }
      });

      const disabledHide = hiddenRows.length === appliedRows.length;
      const disabledShow = !hiddenRows.length;

      return (
        <BulkActionsToolbar
          selectedValues={selectedValues}
          disabledHide={disabledHide}
          disabledShow={disabledShow}
          onDelete={handleBatchDelete}
          onBatchHidden={handleBatchHidden}
          onBatchShow={handleBatchShow}
        />
      );
    },
    [
      data,
      screenState.hidden,
      handleBatchDelete,
      handleBatchHidden,
      handleBatchShow,
    ]
  );
  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };
  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        rows={data?.rows}
        count={data?.rows?.length}
        title={screenTitle}
        loading={isFetching}
        minHeight={TABLE_HEIGHT}
        ready={true}
        selectedRowsToolbarActions={selectedRowsToolbarActions}
        checkboxSelection={true}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        colorGetter={handleRowColor}
        coloredRow={true}
        toolbar={
          <Toolbar
            disabledExport={!visibleSeries.length}
            onCreate={handleCreate}
            onExportAll={handleExportAll}
            onExportVisible={handleExportVisible}
          />
        }
        columnMenuConfig={columnMenuConfig}
        storageKey={'kpi'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
        hideFooterPagination={true}
      >
        <DataGrid.Column
          field={COLUMN_VISIBILITY}
          headerName={'Visible'}
          formatter={renderVisibility}
          type={'__custom'}
          width={64}
          hiddenSeries={screenState.hidden}
          onClickVisibility={handleClickVisibility}
          fixed={true}
          resizable={false}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_METRIC}
          headerName={'Metric'}
          formatter={renderMetric}
          minWidth={394}
          flex={1}
          fixed={true}
          resizable={false}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_GENRES}
          type={'commaList'}
          headerName={'Genres'}
          width={161}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_APPLICATIONS}
          type={'commaList'}
          headerName={'Applications'}
          width={161}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_NETWORKS}
          type={'commaList'}
          headerName={'Networks'}
          width={161}
        />
        <DataGrid.Column
          field={COLUMN_PLATFORMS}
          type={'commaList'}
          headerName={'Platforms'}
          width={161}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_COUNTRIES}
          type={'commaList'}
          headerName={'Countries'}
          width={161}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_MIN}
          type={'dynamic'}
          predicate={predicateColumnType}
          headerName={'Min'}
          width={110}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_AVERAGE}
          type={'dynamic'}
          predicate={predicateColumnType}
          headerName={'Average'}
          width={110}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_MAX}
          type={'dynamic'}
          predicate={predicateColumnType}
          headerName={'Max'}
          width={110}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_TOTAL}
          type={'dynamic'}
          predicate={predicateColumnType}
          headerName={'Total'}
          width={110}
          sortable={false}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          width={158}
          visibleSeries={visibleSeries}
          onDuplicate={handleDuplicate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          limit={3}
          actionsComponent={({ colDef, ...props }) => {
            return (
              <KPIActions {...props} visibleSeries={colDef.visibleSeries} />
            );
          }}
        />
      </DataGrid>
    </React.Fragment>
  );
};

function renderVisibility(_, row, column) {
  const { hiddenSeries, onClickVisibility } = column;
  const { id, dirty } = row;

  const visible = !hiddenSeries.includes(id);
  return (
    <IconButton
      tooltip={visible ? 'Hide' : 'Show'}
      icon={visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
      disabled={dirty}
      onClick={(e) => onClickVisibility(e, { id, state: visible })}
    />
  );
}

function renderMetric(value, row) {
  const { name, description } = value;
  return (
    <Stack>
      <Typography variant={'inherit'} tooltip={description} as={'span'}>
        {name}
      </Typography>
      <Typography variant={'caption'} color={'text.secondary'} as={'span'}>
        {[
          COLUMN_ACTIVITY,
          COLUMN_TRAFFIC,
          COLUMN_REGISTRATIONS,
          COLUMN_FRAUD_TYPE,
        ]
          .map((k) => row[k]?.name)
          .filter((v) => v)
          .join(', ')}
      </Typography>
    </Stack>
  );
}
function predicateColumnType(_, row) {
  const { type } = row.metric;
  return type === 'decimal' ? 'number' : type;
}

export default KPIDataTable;
